import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LogOut, User, Crown, Settings, LayoutDashboard, Shield, Puzzle, Zap } from 'lucide-react';
import { Button } from './ui/button';
import BoxIconOpen from './BoxIconOpen';
import AuthModal from './AuthModal';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NavigationMenu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [menuSettings, setMenuSettings] = useState({
    show_login: true,
    show_signup: true,
    logo_text: 'SENDBOX'
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  
  const { user, isAuthenticated, isPro, isUltimate, isAdmin, logout } = useAuth();

  useEffect(() => {
    loadMenuSettings();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMenuSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/menu`);
      const filteredItems = (res.data.items || []).filter(item => !item.is_button);
      setMenuItems(filteredItems);
      setMenuSettings({
        show_login: res.data.show_login,
        show_signup: res.data.show_signup,
        logo_text: res.data.logo_text || 'SENDBOX'
      });
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const handleLogin = () => {
    setAuthModalTab('login');
    setShowAuthModal(true);
  };

  const handleSignup = () => {
    setAuthModalTab('register');
    setShowAuthModal(true);
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-blue-950/95 backdrop-blur-xl border-b border-blue-500/10">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <BoxIconOpen size={44} />
              <span className="text-2xl font-bold text-white tracking-tight">
                {menuSettings.logo_text}
              </span>
            </Link>

            {/* Navigation + Auth */}
            <div className="flex items-center gap-6">
              {/* Navigation Items */}
              <nav className="hidden md:flex items-center gap-1" ref={dropdownRef}>
                {menuItems
                  .filter(item => item.is_visible !== false)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((item) => (
                    <div key={item.id} className="relative">
                      {item.is_dropdown ? (
                        <>
                          <button
                            onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
                            onMouseEnter={() => setOpenDropdown(item.id)}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-white/80 hover:text-white transition-colors"
                          >
                            {item.label}
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {openDropdown === item.id && item.dropdown_items?.length > 0 && (
                            <div 
                              className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200"
                              onMouseLeave={() => setOpenDropdown(null)}
                            >
                              {item.dropdown_items.map((subItem, index) => (
                                subItem.url.startsWith('/') ? (
                                  <Link
                                    key={index}
                                    to={subItem.url}
                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => setOpenDropdown(null)}
                                  >
                                    {subItem.label}
                                  </Link>
                                ) : (
                                  <a
                                    key={index}
                                    href={subItem.url}
                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    {subItem.label}
                                  </a>
                                )
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        item.url.startsWith('/') ? (
                          <Link
                            to={item.url}
                            className="px-3 py-2 text-sm text-white/80 hover:text-white transition-colors"
                          >
                            {item.label}
                          </Link>
                        ) : (
                          <a
                            href={item.url}
                            className="px-3 py-2 text-sm text-white/80 hover:text-white transition-colors"
                          >
                            {item.label}
                          </a>
                        )
                      )}
                    </div>
                  ))}
              </nav>

              {/* Auth / User Menu */}
              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    {isPro && !isUltimate && (
                      <Crown className="w-4 h-4 text-yellow-400" />
                    )}
                    {isUltimate && (
                      <Zap className="w-4 h-4 text-purple-400" />
                    )}
                    <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showUserMenu && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-slate-900 border border-blue-500/30 rounded-xl shadow-xl py-2 animate-in fade-in slide-in-from-top-2">
                      <div className="px-4 py-2 border-b border-blue-500/20">
                        <p className="text-white font-medium truncate">{user?.name || 'Utilisateur'}</p>
                        <p className="text-blue-300/60 text-sm truncate">{user?.email}</p>
                        {isPro && !isUltimate && (
                          <span className="inline-flex items-center gap-1 mt-1 text-xs text-yellow-400">
                            <Crown className="w-3 h-3" /> Pro
                          </span>
                        )}
                        {isUltimate && (
                          <span className="inline-flex items-center gap-1 mt-1 text-xs text-purple-400">
                            <Zap className="w-3 h-3" /> Ultimate
                          </span>
                        )}
                        {isAdmin && (
                          <span className="inline-flex items-center gap-1 mt-1 ml-2 text-xs text-red-400">
                            <Shield className="w-3 h-3" /> Admin
                          </span>
                        )}
                      </div>
                      
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-blue-200/80 hover:bg-blue-500/10 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="dashboard-link"
                      >
                        <LayoutDashboard className="w-4 h-4" /> Tableau de bord
                      </Link>
                      

                      {!isPro && (
                        <Link
                          to="/pricing"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Crown className="w-4 h-4" /> Passer Pro
                        </Link>
                      )}
                      
                      {isAdmin && (
                        <Link
                          to="/admin/dashboard"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                          data-testid="admin-link"
                        >
                          <Shield className="w-4 h-4" /> Administration
                        </Link>
                      )}
                      
                      <div className="border-t border-blue-500/20 mt-2 pt-2">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <LogOut className="w-4 h-4" /> Déconnexion
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {menuSettings.show_login && (
                    <Button
                      variant="ghost"
                      className="text-white/80 hover:text-white hover:bg-white/10 text-sm font-normal"
                      onClick={handleLogin}
                      data-testid="login-btn"
                    >
                      Connexion
                    </Button>
                  )}
                  {menuSettings.show_signup && (
                    <Button
                      className="bg-white text-gray-900 hover:bg-gray-100 font-medium text-sm px-4 rounded-lg"
                      onClick={handleSignup}
                      data-testid="signup-btn"
                    >
                      S'inscrire
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab={authModalTab}
      />
    </>
  );
};

export default NavigationMenu;
